import {
  composeExitPlacement,
  fetchExitPlacementComposePreview,
} from "../services/reportsApi";
import type { ReportStrategyPolicyVersion } from "../types/reports";
import type {
  ExitPlacementComposePreviewResponse,
  ExitPlacementComposeRequest,
  ExitPlacementComposeResponse,
  SimulateLegs,
} from "../types/managementExplorer";
import type { ComposeOutputMode } from "../types/reportStrategyPine";

export type ExitPlacementSliceRunRef = {
  exitPlacementSlices?: Array<{ slice_id?: string }>;
};

export function isRegimeEntryPolicyVersion(v: ReportStrategyPolicyVersion): boolean {
  if (v.policy_schema_version === "policy_tree.v1") return true;
  if (v.policy_schema_version === "policy_tree.bundle.v1") return true;
  if (v.policy_schema_version === "policy_tree.v2") return false;

  const tree = v.policy_tree as
    | { schema_version?: string; action?: unknown; root?: unknown; rules?: unknown[] }
    | null
    | undefined;
  if (!tree || typeof tree !== "object") return false;
  if (tree.schema_version === "policy_tree.v1") return true;
  if (tree.schema_version === "policy_tree.bundle.v1") return true;
  if (tree.schema_version === "policy_tree.v2") return false;
  if ("action" in tree && "root" in tree) return true;
  return false;
}

/** Regime keep/veto policy version ids to pass as `entry_policy_version_ids`. */
export function resolveRegimeEntryPolicyVersionIds(
  versions: ReportStrategyPolicyVersion[],
  opts?: {
    currentPolicyVersionId?: string | null;
    lastComposePolicyVersionIds?: string[] | null;
  }
): string[] {
  const regimeIds = new Set(versions.filter(isRegimeEntryPolicyVersion).map((v) => v.id));

  const fromLastCompose = (opts?.lastComposePolicyVersionIds ?? []).filter((id) => regimeIds.has(id));
  if (fromLastCompose.length) return fromLastCompose;

  const currentId = opts?.currentPolicyVersionId?.trim();
  if (currentId && regimeIds.has(currentId)) return [currentId];

  return [];
}

export function sliceIdsFromComparisonRun(run: ExitPlacementSliceRunRef): string[] {
  const fromSlices = (run.exitPlacementSlices ?? [])
    .map((s) => String(s.slice_id ?? "").trim())
    .filter(Boolean);
  if (fromSlices.length) return fromSlices;
  return [];
}

export function exitPlacementPreviewSegmentSummary(
  preview: ExitPlacementComposePreviewResponse | null | undefined
): string | null {
  if (!preview) return null;
  const counts = preview.segment_counts;
  const entry =
    counts?.entry_filter ?? preview.entry_segment_count ?? null;
  const mgmt =
    counts?.management ?? preview.management_segment_count ?? null;
  const parts: string[] = [];
  if (typeof entry === "number") parts.push(`${entry} entry`);
  if (typeof mgmt === "number") parts.push(`${mgmt} management`);
  return parts.length ? parts.join(" · ") : null;
}

export function exitPlacementComposeWarnings(
  preview: ExitPlacementComposePreviewResponse | null | undefined
): string[] {
  if (!preview?.warnings?.length) return [];
  return preview.warnings
    .map((w) => String(w.message ?? "").trim())
    .filter(Boolean);
}

export function exitPlacementComposeBlockers(
  preview: ExitPlacementComposePreviewResponse | null | undefined
): string[] {
  if (!preview) return ["Compose preview not loaded."];
  const blockers: string[] = [];

  if (preview.combination_ok === false) {
    blockers.push("Regime entry segments cannot merge with the selected exit slices.");
  }
  if (preview.management_hooked === false) {
    blockers.push("Exit management hooks are not ready to attach to your strategy.");
  }
  const readiness = String(preview.exit_hook_readiness ?? "").trim();
  if (readiness && readiness !== "ready") {
    blockers.push(
      readiness === "partial"
        ? "Exit hooks are only partially supported — use named stop= / limit= on strategy.exit()."
        : readiness === "unsupported"
          ? "Exit hooks are unsupported on this Pinescript."
          : `Exit hook readiness: ${readiness}.`
    );
  }
  if (preview.compile_ok === false) {
    blockers.push("Dry-run compile failed — fix Pinescript or policy before composing.");
  }

  blockers.push(...exitPlacementComposeWarnings(preview));
  return blockers;
}

export function isExitPlacementComposeReady(
  preview: ExitPlacementComposePreviewResponse | null | undefined
): boolean {
  if (!preview) return false;
  if (preview.combination_ok === false) return false;
  if (preview.management_hooked === false) return false;
  if (preview.compile_ok === false) return false;
  return String(preview.exit_hook_readiness ?? "").trim() === "ready";
}

export type ExitPlacementComposePreviewParams = {
  reportId: string;
  sliceIds: string[];
  strategyId?: string;
  simulateLegs?: SimulateLegs;
  entryPolicyVersionIds?: string[];
  pinescript?: string;
};

export async function loadExitPlacementComposePreview(
  params: ExitPlacementComposePreviewParams
): Promise<ExitPlacementComposePreviewResponse> {
  const {
    reportId,
    sliceIds,
    strategyId,
    simulateLegs,
    entryPolicyVersionIds,
    pinescript,
  } = params;
  return fetchExitPlacementComposePreview(reportId, {
    slice_ids: sliceIds,
    ...(strategyId?.trim() ? { strategy_id: strategyId.trim() } : {}),
    ...(simulateLegs ? { simulate_legs: simulateLegs } : {}),
    ...(entryPolicyVersionIds?.length ? { entry_policy_version_ids: entryPolicyVersionIds } : {}),
    ...(pinescript?.trim() ? { pinescript: pinescript.trim() } : {}),
  });
}

export type ExitPlacementComposeParams = {
  reportId: string;
  sliceIds: string[];
  strategyId: string;
  simulateLegs?: SimulateLegs;
  entryPolicyVersionIds?: string[];
  outputMode?: ComposeOutputMode;
  persistPolicyVersion?: boolean;
};

export async function runExitPlacementCompose(
  params: ExitPlacementComposeParams
): Promise<ExitPlacementComposeResponse> {
  const body: ExitPlacementComposeRequest = {
    slice_ids: params.sliceIds,
    strategy_id: params.strategyId,
    output_mode: params.outputMode ?? "merge_into_strategy",
    persist_policy_version: params.persistPolicyVersion ?? true,
    ...(params.simulateLegs ? { simulate_legs: params.simulateLegs } : {}),
    ...(params.entryPolicyVersionIds?.length
      ? { entry_policy_version_ids: params.entryPolicyVersionIds }
      : {}),
  };
  return composeExitPlacement(params.reportId, body);
}

/** Only merge_into_strategy applies SL/TP hooks on chart for Exit Placement. */
export const EXIT_PLACEMENT_COMPOSE_MODES_VIEW_ONLY = [
  "separate_indicator",
  "append_policy_to_indicator",
] as const;
